/**
 * `ParameterTrendChart` — Recharts line chart for one manual-test
 * parameter: amber manual-reading line with dots, optional sky-blue
 * Apex/Trident overlay, and a green ideal-range band.
 */
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ManualReading } from '@reefapp/shared';

import type { TimeSeriesPoint } from '../lib/timeseries-api';

interface Row {
  ts: number;
  manual?: number;
  apex?: number;
}

export function ParameterTrendChart({
  readings,
  apexPoints,
  idealRange,
  unit,
  parameter,
}: {
  /** Manual readings for ONE parameter (any order). */
  readings: readonly ManualReading[];
  /** Apex/Trident overlay points; empty = no overlay drawn. */
  apexPoints: readonly TimeSeriesPoint[];
  idealRange: { min: number; max: number } | null;
  unit: string;
  parameter: string;
}) {
  const data = useMemo<Row[]>(() => {
    const rows: Row[] = [];
    for (const r of readings) {
      const ts = Date.parse(r.timestamp);
      if (!Number.isFinite(ts) || !Number.isFinite(r.value)) continue;
      rows.push({ ts, manual: r.value });
    }
    for (const p of apexPoints) {
      if (!Number.isFinite(p.value)) continue;
      rows.push({ ts: p.ts, apex: p.value });
    }
    return rows.sort((a, b) => a.ts - b.ts);
  }, [readings, apexPoints]);

  if (data.length < 2) return null;

  const first = data[0]!.ts;
  const last = data[data.length - 1]!.ts;
  const spanMs = last - first;
  const formatTs =
    spanMs <= 2 * 24 * 60 * 60 * 1_000
      ? (ts: number) =>
          new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : (ts: number) =>
          new Date(ts).toLocaleDateString([], { month: 'numeric', day: 'numeric' });

  const hasApex = apexPoints.length > 0;

  return (
    <div
      className="mb-6 p-4 bg-reef-900 border border-reef-800 rounded-xl"
      data-testid="parameter-trend-chart"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <h3 className="text-sm font-semibold text-reef-200">{parameter} Trend</h3>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="flex items-center gap-1.5 text-amber-300">
            <span className="inline-block h-0.5 w-4 bg-amber-400" /> Your tests
          </span>
          {hasApex ? (
            <span className="flex items-center gap-1.5 text-sky-300">
              <span className="inline-block h-0.5 w-4 bg-sky-400" /> Apex / Trident
            </span>
          ) : null}
          {idealRange ? (
            <span className="flex items-center gap-1.5 text-emerald-300">
              <span className="inline-block h-2 w-4 bg-emerald-500/20 border border-emerald-500/40" />{' '}
              Ideal
            </span>
          ) : null}
        </div>
      </div>
      <div className="h-52 sm:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -6 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis
              dataKey="ts"
              type="number"
              domain={[first, last]}
              tickFormatter={formatTs}
              tick={{ fill: '#64748b', fontSize: 10 }}
              stroke="#334155"
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fill: '#64748b', fontSize: 10 }}
              stroke="#334155"
              width={48}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: '#94a3b8' }}
              labelFormatter={(ts) => new Date(Number(ts)).toLocaleString()}
              formatter={(value, name) => [
                `${Number(value)} ${unit}`.trim(),
                name === 'manual' ? 'Your test' : 'Apex / Trident',
              ]}
            />
            {idealRange ? (
              <ReferenceArea
                y1={idealRange.min}
                y2={idealRange.max}
                fill="#10b981"
                fillOpacity={0.08}
                stroke="#10b981"
                strokeOpacity={0.25}
                strokeDasharray="4 4"
              />
            ) : null}
            {hasApex ? (
              <Line
                type="monotone"
                dataKey="apex"
                stroke="#38bdf8"
                strokeWidth={1.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="manual"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ r: 3, fill: '#f59e0b' }}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
